import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { getRouteUrl } from "us.helper";
import {
  $Collapse,
  $Empty,
  InfiniteScrolling,
  $Checkbox,
  $Tooltip,
  $Popconfirm,
  $TextArea,
  $Button,
} from "us.common/components";
import { IRootState } from "us.collection/interfaces";
import { managePayment } from "us.collection.economy/actions";
import {
  DebtorOtherCasesStatus,
  INITIAL_ADVANCED_FILTERS,
  PaymentStates,
  PeriodType,
} from "us.collection.economy/constants/Payments";
import { PaymentHelper } from "us.collection.economy/components/ManagePayments/Functions";
import {
  ActionTypes,
  IPayment,
  IPaymentLayout,
} from "us.collection.economy/components/ManagePayments/interface";
import {
  Header,
  SubHeader,
  MappedWithOther,
  PaymentBase,
  PaymentItems,
  BaseHeader,
  Footer,
  ReturnWithOther,
  Row,
} from "./components";
import { BoxIcons, ExclamationCircleFilled, IconTypes } from "us.icons";
import { useFormikContext } from "formik";
import { GetPayments } from "../../Repository/ManagePayments";

const { getPaymentAccounts, searchPayments, deletePayment } = managePayment;

/**
 * Component which manage payments
 */
const PaymentLayout: React.FC<PropsFromRedux & IPaymentLayout> = ({
  activeId = 0,
  values,
  data,
  hasMorePayments,
  onSelectPayment,
  onItemCallAction,
  onViewLinkedPayment,
  onViewPaymentDetails,
  onLoadMore,
  onScrollToFocusedItem,
  onBackToTop,
  deletePayment,
}: PropsFromRedux & IPaymentLayout) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const params = queryString.parse(search);
  const [removePopoverStatus, setRemovePopoverStatus] = useState(false);
  const { setFieldValue } = useFormikContext<any>();

  /**
   * @function
   * @description handle payment deletion
   */
  const handleDeletePayments = (paymentIds: number[]) => {
    const {
      debtorOtherCasesStatus,
      isMapped,
      isReturnToDebtor,
      periodType,
      paymentId,
      isExceeded,
    } = values;
    // no content scroll to top if in bottom
    window.scrollTo({ top: 0, behavior: "smooth" });
    PaymentHelper.setLinkedPaymentId(undefined);
    const shouldHasMapped =
      debtorOtherCasesStatus !== DebtorOtherCasesStatus.ALL && isExceeded
        ? false
        : isMapped;
    const shouldHasReturned =
      debtorOtherCasesStatus !== DebtorOtherCasesStatus.ALL && isExceeded
        ? false
        : isReturnToDebtor;
    const requestParams = {
      ...values,
      ...INITIAL_ADVANCED_FILTERS,
      paymentId: periodType === PeriodType.ALL ? paymentId : "",
      paymentStates:
        periodType === PeriodType.ALL
          ? []
          : PaymentHelper.getPaymentStatus({
              ...values,
              isMapped: shouldHasMapped,
              isReturnToDebtor: shouldHasReturned,
            }),
      isMapped: shouldHasMapped,
      isReturnToDebtor: shouldHasReturned,
      debtorOtherCasesStatus: !isExceeded
        ? DebtorOtherCasesStatus.ALL
        : debtorOtherCasesStatus,
    };
    const searchFilters = GetPayments.call(requestParams);

    deletePayment &&
      deletePayment({
        deleteParams: {
          paymentIds: paymentIds,
          comment: values?.deleteComment,
        },
        searchParams: searchFilters,
      });
    setFieldValue("deleteComment", "");
    setFieldValue("paymentIds", []);
  };

  /**
   * @function
   * @description handle payment selection
   */
  const handlePaymentSelection = (checked: any[], paymentId: number) => {
    let ids = values?.paymentIds;
    if (checked) {
      // Add paymentId if not already in the array
      if (!ids.includes(paymentId)) {
        ids = [...ids, paymentId];
      }
    } else {
      // Remove paymentId if it exists in the array
      ids = ids.filter((id: any) => id !== paymentId);
    }
    setFieldValue("paymentIds", ids);
  };

  return (
    <>
      <InfiniteScrolling
        pageLength={data.length}
        handleNextDataLoad={() => {
          onLoadMore();
        }}
        isMoreData={hasMorePayments}
        isExtraOptions
        enableBackToTop
        isDivScroll
        onFocusToItem={onScrollToFocusedItem}
        onBackToTop={onBackToTop}
      >
        {data && data.length > 0 && (
          <$Collapse
            ghost
            accordion
            defaultActiveKey={activeId}
            activeKey={params?.open === "true" ? Number(params?.id) : undefined}
            onChange={(value: string | string[]) =>
              onSelectPayment(value ? Number(value) : undefined)
            }
            collapsible={"header"}
            className="map-payment-collapsed"
          >
            {data.map((mainPaymentItem: IPayment, index: number) => {
              const {
                mainStatus,
                mainStatusAmount,
                tagClass,
                tagName,
                headerActions,
                subHeaderActions,
                mappedItems,
                returnedItems,
                isMappedWithOther,
                isReturnedWithOther,
                hasPaymentDistribution,
                isFullyReturn,
              } = new PaymentHelper(mainPaymentItem);
              const {
                paymentId,
                paymentDistribution,
                mappedSum,
                returnedToDebtorSum,
                linkedPaymentId,
                isDeleteAvailable,
                isCaseClosed,
                isDeletionExpired,
                isManuallyAdded
              } = mainPaymentItem;
              return (
                <$Collapse.Panel
                  key={paymentId}
                  id={`${paymentId}`}
                  header={
                    <>
                      <$Checkbox
                        id={`${paymentId}`}
                        key={paymentId}
                        data-testid="selection-box"
                        checked={values?.paymentIds.includes(paymentId)}
                        disabled={
                          !isDeleteAvailable ||
                          isCaseClosed ||
                          isDeletionExpired ||
                          !isManuallyAdded ||
                          (linkedPaymentId > 0 && mainStatus === PaymentStates.UNKNOWN) 
                        }
                        onChange={(e: any) => {
                          e.stopPropagation();
                          handlePaymentSelection(e?.target?.checked, paymentId);
                        }}
                      />
                      <BaseHeader
                        data={mainPaymentItem}
                        priorityTag={PaymentHelper.getPriorityTag(
                          paymentDistribution,
                          values.paymentStates
                        )}
                        onViewLinkedPayment={() => {
                          PaymentHelper.setLinkedPaymentId(linkedPaymentId);
                          onViewLinkedPayment({
                            linkedPaymentId,
                          });
                        }}
                        onDeletePayment={() => {
                          handleDeletePayments([paymentId]);
                        }}
                        mainStatus={mainStatus}
                      />
                    </>
                  }
                >
                  <PaymentBase
                    amount={mainStatusAmount}
                    tagName={tagName}
                    tagClass={tagClass}
                    isUnknownOld={
                      linkedPaymentId > 0 &&
                      mainStatus === PaymentStates.UNKNOWN
                    }
                  >
                    {/*render if has header with action*/}
                    {![
                      PaymentStates.UNKNOWN,
                      PaymentStates.EXCEEDED,
                      PaymentStates.RETURNED,
                    ].includes(mainStatus) && (
                      <Header
                        actions={
                          linkedPaymentId > 0 &&
                          mainStatus === PaymentStates.UNKNOWN
                            ? []
                            : headerActions
                        }
                        onAction={(action) =>
                          onItemCallAction({
                            action,
                            mainPaymentItem,
                          })
                        }
                        isCaseClosed={isCaseClosed}
                      />
                    )}
                    {/*render if has sub header with some actions(Apportion/Revert/Edit etc)*/}
                    {[PaymentStates.UNKNOWN, PaymentStates.EXCEEDED].includes(
                      mainStatus
                    ) && (
                      <SubHeader
                        actions={
                          linkedPaymentId > 0 &&
                          mainStatus === PaymentStates.UNKNOWN
                            ? []
                            : subHeaderActions
                        }
                        label={`${
                          mainStatus === PaymentStates.UNKNOWN
                            ? t(
                                "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.UNKNOWN_AMOUNT"
                              )
                            : t(
                                "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.EXCEEDED_AMOUNT"
                              )
                        }`}
                        onAction={(action) =>
                          onItemCallAction({
                            action,
                            mainPaymentItem,
                          })
                        }
                        amount={mainStatusAmount}
                      />
                    )}
                    {/*render if has mapped items*/}
                    {mainStatus === PaymentStates.MAPPED && (
                      <PaymentItems
                        isCaseClosed={isCaseClosed}
                        payments={mappedItems}
                      />
                    )}
                    {/*render if has returned items*/}
                    {mainStatus === PaymentStates.RETURNED && (
                      <div className="map-item-table pr-2">
                        {returnedItems?.map((payment: any) => {
                          const {
                            arItemNo,
                            amount,
                            transactionItemTypeId,
                            returnedDate,
                          } = payment;
                          return (
                            <Row
                              key={arItemNo}
                              amount={amount}
                              paymentId={arItemNo}
                              itemTypeId={transactionItemTypeId}
                              label={
                                transactionItemTypeId == 25
                                  ? t(
                                      "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.RETURN_FEE"
                                    )
                                  : `${t(
                                      "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.RETURN_TO"
                                    )} ${mainPaymentItem.accountNo ?? ""}`
                              }
                              returnedDate={returnedDate}
                              onRevert={() => {
                                onItemCallAction({
                                  action: ActionTypes.REVERT_TO_PAYMENT,
                                  payment,
                                  mainPaymentItem,
                                });
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    {/*render if has returned items with other status*/}
                    {isReturnedWithOther && (
                      <ReturnWithOther
                        amount={returnedToDebtorSum}
                        accountNo={mainPaymentItem.accountNo}
                        items={returnedItems}
                        onRevertItem={(payment) => {
                          onItemCallAction({
                            action: ActionTypes.REVERT_TO_PAYMENT,
                            payment,
                            mainPaymentItem,
                          });
                        }}
                        isCaseClosed={isCaseClosed}
                      />
                    )}
                    {/*render if has mapped items with other status*/}
                    {isMappedWithOther && (
                      <MappedWithOther
                        amount={mappedSum}
                        items={mappedItems}
                        onRevertAll={(payment) => {
                          //'Revert All'
                          onItemCallAction({
                            action: ActionTypes.REVERT_PAYMENT,
                            payment,
                            mainPaymentItem,
                          });
                        }}
                        onRevertItem={(payment) => {}}
                        isCaseClosed={isCaseClosed}
                      />
                    )}
                    {/*render footer component - not includes in Exceeded and Unknown */}
                    {!(
                      [PaymentStates.EXCEEDED, PaymentStates.UNKNOWN].includes(
                        mainStatus
                      ) && !isMappedWithOther
                    ) &&
                      !isFullyReturn && (
                        <Footer
                          onViewDistributions={() =>
                            getRouteUrl.paymentDistribution(paymentId)
                          }
                          onViewPaymentDetails={() =>
                            onViewPaymentDetails(mainPaymentItem)
                          }
                          withViewDistribution={hasPaymentDistribution}
                        />
                      )}
                  </PaymentBase>
                </$Collapse.Panel>
              );
            })}
          </$Collapse>
        )}
      </InfiniteScrolling>
      <$Tooltip
        title={t("US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.REMOVE_SELECTED_ITEMS")}
      >
        <$Popconfirm
          title={
            <div>
              <p style={{ width: "250px" }}>
                {t(
                  "US.COLLECTION.ECONOMY:MANAGE_PAYMENTS.PLEASE_COMMENT_BELOW_AND_CONFIRM_THE_ACTION_REMOVE"
                )}
              </p>
              <$TextArea
                style={{ width: "250px" }}
                required
                placeholder={t("COMMON.COMMENT")}
                name="deleteComment"
                autoSize={{ minRows: 2, maxRows: 5 }}
                maxLength={150}
              />
            </div>
          }
          onVisibleChange={(visible: any) => {
            if (!visible) {
              setRemovePopoverStatus(false);
            }
          }}
          okButtonProps={{
            type: "primary",
            danger: true,
            disabled: !values?.deleteComment,
          }}
          destroyTooltipOnHide
          placement="topLeft"
          onConfirm={() => {
            handleDeletePayments(values?.paymentIds);
            setRemovePopoverStatus(false);
          }}
          onCancel={() => {
            setRemovePopoverStatus(false);
          }}
          okText={t("COMMON.REMOVE")}
          cancelText={t("COMMON.CANCEL")}
          visible={removePopoverStatus}
          icon={<ExclamationCircleFilled className="text-error" />}
        >
          <$Button
            name="removeBtn"
            onClick={() => setRemovePopoverStatus(!removePopoverStatus)}
            shape="circle"
            icon={
              <BoxIcons
                type={IconTypes.BOX_ICON}
                name="delete"
                style={{ fontSize: 12 }}
              />
            }
            danger
            className="delete-selected-items"
            data-testid="remove-all-btn"
            disabled={values?.paymentIds?.length <= 0}
          />
        </$Popconfirm>
      </$Tooltip>

      {!data ||
        (data.length === 0 && <$Empty image={$Empty.PRESENTED_IMAGE_SIMPLE} />)}
    </>
  );
};

const mapStateToProps = (state: IRootState) => {
  const { managePayment, common } = state;

  const { payments, paymentsWithAcc, paymentAccounts, paymentDistribution } =
    managePayment;
  const { currentDateFormat, currentLanguage, currentCurrency } = common;
  return {
    payments,
    paymentsWithAcc,
    paymentAccounts,
    currentDateFormat,
    currentLanguage,
    currentCurrency,
    paymentDistribution,
  };
};

const mapDispatchToProps = {
  getPaymentAccounts,
  searchPayments,
  deletePayment,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(PaymentLayout);
